/**
 * Form drafts are a convenience (a reload or a wrong click should not cost someone a half-filled
 * signup form), but browser storage is readable by anyone with the browser profile and by any
 * script on the page — so secrets and identity data are never written to it.
 *
 * Both signup drafts used to store the chosen password (and its confirmation) in plain text on
 * every keystroke, where it stayed if signup was abandoned. Every draft write and read goes through
 * sanitizeDraft(), and scrubStoredDrafts() removes what earlier releases already left behind.
 */

/** Never persisted, in any draft. Extend here, nowhere else. */
export const SENSITIVE_DRAFT_KEYS = ['password', 'confirmPassword', 'dob', 'ssn'] as const;

/** sessionStorage draft of EMT signup Step 1 (src/components/EMTRegStep1.tsx). */
export const EMT_REG_STEP1_STORAGE_KEY = 'emtRegStep1';
/** localStorage drafts written by src/hooks/useDraft.ts all use this prefix. */
const LOCAL_DRAFT_PREFIX = 'draft:';

export function sanitizeDraft<T>(value: T): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const copy = { ...(value as Record<string, unknown>) };
  for (const key of SENSITIVE_DRAFT_KEYS) delete copy[key];
  return copy as T;
}

function scrubKey(storage: Storage, key: string): void {
  const raw = storage.getItem(key);
  if (!raw) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    storage.removeItem(key); // unreadable draft: nothing worth keeping, and it may hold a secret
    return;
  }
  if (!parsed || typeof parsed !== 'object') return;
  if (!SENSITIVE_DRAFT_KEYS.some((k) => k in (parsed as Record<string, unknown>))) return;
  storage.setItem(key, JSON.stringify(sanitizeDraft(parsed)));
}

/**
 * Remove sensitive fields from every draft already in this browser. Runs once at app start, so
 * someone who abandoned signup on an older release is cleaned up on their next visit to any page —
 * not only if they happen to reopen the form.
 */
export function scrubStoredDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LOCAL_DRAFT_PREFIX)) keys.push(key);
    }
    for (const key of keys) scrubKey(localStorage, key);
  } catch {
    // storage unavailable (private mode / blocked) — nothing stored, nothing to scrub
  }
  try {
    scrubKey(sessionStorage, EMT_REG_STEP1_STORAGE_KEY);
  } catch {
    // ignore
  }
}
